import { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppSelector } from "app/hooks/useStore";
// Selectors
import { selectCurrentDate, selectCurrentView } from "app/store/CalendarEvents/CalendarEventsSelectors";
// MUI
import { Box, Tooltip, Typography } from "@mui/material";
// Icons
import {
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
// Components
import { Button, IconButton, Select } from "app/components/Mui";

type Props = {
  calendarRef: React.RefObject<any>;
};

const CalendarToolbar: FC<Props> = ({ calendarRef }) => {
  const { t } = useTranslation();

  const currentDate = useAppSelector(selectCurrentDate);
  const currentView = useAppSelector(selectCurrentView);
  const [calendarApi, setCalendarApi] = useState<any>(null);

  useEffect(() => {
    if (calendarRef.current) {
      setCalendarApi(calendarRef.current.getApi());
    }
  }, [calendarRef]);

  const handlePrev = () => {
    if (!calendarApi) return;
    calendarApi.prev();
  };

  const handleNext = () => {
    if (!calendarApi) return;
    calendarApi.next();
  };

  const handleToday = () => {
    if (!calendarApi) return;
    calendarApi.today();
  };

  const handleViewChange = (view: string) => {
    if (!calendarApi) return;
    calendarApi.changeView(view);
  };

  const formattedDate = useMemo(() => {
    if (!currentDate) return "";

    switch (currentView) {
      case "dayGridMonth":
        return dayjs(currentDate).format("MMMM YYYY");
      case "timeGridWeek":
        const start = dayjs(currentDate).startOf("week").format("MMM D");
        const end = dayjs(currentDate).endOf("week").format("MMM D, YYYY");
        return `${start} - ${end}`;
      case "timeGridDay":
        return dayjs(currentDate).format("MMMM D, YYYY");
      case "multiMonthThree":
      case "multiMonthYear":
        return dayjs(currentDate).format("YYYY");
      default:
        return dayjs(currentDate).format("DD MMM YYYY");
    }
  }, [currentDate, currentView]);

  const prevLabel = useMemo(() => {
    switch (currentView) {
      case "dayGridMonth":
        return t('buttons.prevMonth');
      case "timeGridWeek":
        return t('buttons.prevWeek');
      case "timeGridDay":
        return t('buttons.prevDay');
      case "multiMonthThree":
        return t('buttons.prevQuarter');
      case "multiMonthYear":
        return t('buttons.prevYear');
      default:
        return t('buttons.prev');
    }
    // eslint-disable-next-line
  }, [currentView]);

  const nextLabel = useMemo(() => {
    switch (currentView) {
      case "dayGridMonth":
        return t('buttons.nextMonth');
      case "timeGridWeek":
        return t('buttons.nextWeek');
      case "timeGridDay":
        return t('buttons.nextDay');
      case "multiMonthThree":
        return t('buttons.nextQuarter');
      case "multiMonthYear":
        return t('buttons.nextYear');
      default:
        return t('buttons.next');
    }
    // eslint-disable-next-line
  }, [currentView]);

  const viewOptions = useMemo(() => [
    { id: "timeGridDay", name: t('labels.day') },
    { id: "timeGridWeek", name: t('labels.week') },
    { id: "dayGridMonth", name: t('labels.month') },
    { id: "multiMonthThree", name: t('labels.quarter') },
    { id: "multiMonthYear", name: t('labels.year') },
  ], [t]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: 'center', gap: 1 }}>
        <Button
          name="calendar-today-button"
          size="small"
          variant="outlined"
          onClick={handleToday}
        >{t('labels.today')}</Button>
        <Tooltip title={prevLabel}>
          <IconButton
            name="calendar-prev-button"
            size="small"
            onClick={handlePrev}
          ><ArrowBackIosNewIcon /></IconButton>
        </Tooltip>
        <Tooltip title={nextLabel}>
          <IconButton
            name="calendar-next-button"
            size="small"
            onClick={handleNext}
          ><ArrowForwardIosIcon /></IconButton>
        </Tooltip>
      </Box>

      <Typography variant="h6">
        {formattedDate}
      </Typography>

      <Select
        size="small"
        value={currentView}
        onChange={(e) => handleViewChange(e.target.value)}
        options={viewOptions}
        sx={{ width: 150 }}
      />
    </Box>
  );
};

export default CalendarToolbar;
